import { SESSION } from '@/constants/constants'

/**
 * What the tenant's accounting system can do.
 *
 * Not every ledger behind One Sales offers everything the app does. Xero has no
 * consignment and no stock — those are 404s on its API, not permissions we lack
 * — so offering the screens to a Xero tenant means offering something that
 * cannot work. This is what decides whether they appear at all.
 *
 * Fetched once after login and cached. The accounting system is the
 * `accounting_driver` configuration row; the configuration screen refreshes
 * this cache right after changing it, so the menu follows the new system
 * without a re-login.
 *
 * Hiding a menu is a courtesy, not a control. The route is still there, and the
 * `capability` middleware on the API is what actually refuses.
 */

export function setCapabilities(payload) {
  localStorage.setItem(SESSION.ACCOUNTING_CAPABILITIES, JSON.stringify(payload || {}))
}

export function clearCapabilities() {
  localStorage.removeItem(SESSION.ACCOUNTING_CAPABILITIES)
}

function read() {
  const raw = localStorage.getItem(SESSION.ACCOUNTING_CAPABILITIES)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

/** Which accounting system this tenant runs — 'autocount' | 'sqlaccount' | 'xero' | 'quickbooks'. */
export function accountingDriver() {
  return read()?.driver || null
}

/**
 * 'full' (AutoCount, SQL Account: agents, UOMs, SO/DO and consignment live in
 * the ledger, so One Sales shows them read-only) or 'lite' (Xero, QuickBooks:
 * One Sales owns agents, UOMs and price tiers itself). Null until fetched.
 *
 * Prefer `supports()` for a single capability; this is for screens that need
 * the whole posture at once, such as the product page deciding which fields
 * are editable.
 */
export function accountingFamily() {
  return read()?.family || null
}

/**
 * True when the accounting system supports the named capability.
 *
 * A falsy capability means "nothing to check", so the item is always shown —
 * the same convention `can()` uses for permissions.
 *
 * Defaults to true when nothing has been fetched yet. Showing a menu that
 * turns out to be unavailable is recoverable; hiding half the app because one
 * request had not landed yet is not, and the API still refuses either way.
 */
export function supports(capability) {
  if (!capability) return true

  const caps = read()
  if (!caps || !caps.supports) return true

  return caps.supports[capability] !== false
}

/**
 * True only when the accounting system has EXPLICITLY advertised an optional
 * module — the opposite default from supports(). A missing menu is
 * recoverable; asking a backend for a module it never claimed to have is a
 * 404 on every page that asks, and there is nothing to recover from.
 */
export function hasModule(name) {
  const caps = read()
  return !!(caps && caps.supports && caps.supports[name] === true)
}
